import { useState, useEffect } from 'react';
import { usePlans } from '../hooks/usePlans';
import { useAuth } from '../contexts/AuthContext';
import { PLANBASE_CONFIG } from '../config';
import { usePurchases } from '../contexts/PurchasesContext';
import { toast } from 'sonner';
import type { RCPackage } from '../lib/purchasesService';

import { ArrowPathIcon, ArrowUturnLeftIcon, CheckIcon } from '@heroicons/react/24/outline';

// ── Pricing data ──────────────────────────────────────────────────────────────

const PLAN_FEATURES: Record<string, string[]> = {
  free: [
    '1 project',
    'Up to 10 images per project',
    'Basic discovery browsing',
    'Public portfolio',
  ],
  pro: [
    'Up to 50 projects',
    'Up to 20 images per project',
    'Unlimited discovery browsing',
    'Public portfolio',
    'Priority support',
    'Collections',
    'AI assistant (Orbit)',
  ],
  enterprise: [
    'Unlimited projects',
    'Up to 50 images per project',
    'Unlimited discovery browsing',
    'Public portfolio',
    'Priority support',
    'Collections',
    'AI assistant (Orbit)',
    'Custom branding',
    'Advanced analytics',
    'API access',
  ],
};

export default function Pricing() {
  const { plans } = usePlans();
  const { user } = useAuth();
  const { plan, planLoading, offering, purchase, restore, iapAvailable, refreshPlan } = usePurchases();

  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const currentTier = plan.tier;

  // ── Native IAP purchase ────────────────────────────────────────────────────

  const handleNativePurchase = async (pkg: RCPackage) => {
    setPurchasing(pkg.identifier);
    try {
      const result = await purchase(pkg);
      if (result.success) {
        toast.success('Purchase successful! Welcome to ' + (result.plan?.tier === 'enterprise' ? 'Enterprise' : 'Pro') + '!');
      } else if (!result.userCancelled) {
        toast.error(result.error || 'Purchase failed. Please try again.');
      }
    } finally {
      setPurchasing(null);
    }
  };

  // ── Web Stripe checkout ────────────────────────────────────────────────────

  const handleWebCheckout = async (planId: string) => {
    if (!user) return;
    setPurchasing(planId);
    try {
      const token = localStorage.getItem('planbase_token');
      const res = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
          'X-Project-ID': PLANBASE_CONFIG.projectId,
          ...(token ? { 'X-User-Token': token } : {}),
        },
        body: JSON.stringify({
          plan_id: planId,
          customer_email: user.email,
          success_url: `${window.location.origin}/pricing?success=1`,
          cancel_url: window.location.href,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
      }
    } catch {
      toast.error('Checkout failed. Please try again.');
    } finally {
      setPurchasing(null);
    }
  };

  // ── Billing portal (web only) ─────────────────────────────────────────────

  const handleManageBilling = async () => {
    if (!user) return;
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('planbase_token');
      const res = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/billing/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
          'X-Project-ID': PLANBASE_CONFIG.projectId,
          ...(token ? { 'X-User-Token': token } : {}),
        },
        body: JSON.stringify({
          customer_email: user.email,
          return_url: window.location.href,
        }),
      });
      const data = await res.json();
      if (data.url || data.portal_url) {
        window.location.href = data.url || data.portal_url;
      } else {
        toast.error('Could not open billing portal. Please contact support.');
      }
    } catch {
      toast.error('Failed to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  // ── Restore purchases (native only) ──────────────────────────────────────

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const result = await restore();
      if (result.success && result.plan?.isActive) {
        toast.success('Purchases restored successfully!');
      } else if (result.success) {
        toast.info('No active purchases found.');
      } else {
        toast.error(result.error || 'Restore failed.');
      }
    } finally {
      setRestoring(false);
    }
  };

  // Refresh plan on success redirect (web)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search.includes('success=1')) {
      refreshPlan();
    }
  }, []);

  // ── Resolve packages from RC offering ────────────────────────────────────

  const getPackageForTier = (tier: string): RCPackage | null => {
    if (!offering) return null;
    const pkgs = offering.availablePackages;
    if (tier === 'pro') {
      return billingInterval === 'year'
        ? pkgs.find(p => p.identifier.includes('pro') && p.identifier.includes('annual')) ?? offering.annual ?? null
        : pkgs.find(p => p.identifier.includes('pro') && p.identifier.includes('monthly')) ?? offering.monthly ?? null;
    }
    if (tier === 'enterprise') {
      return billingInterval === 'year'
        ? pkgs.find(p => p.identifier.includes('enterprise') && p.identifier.includes('annual')) ?? null
        : pkgs.find(p => p.identifier.includes('enterprise') && p.identifier.includes('monthly')) ?? null;
    }
    return null;
  };

  const getPriceDisplay = (tier: string): string => {
    if (tier === 'free') return '$0';
    const pkg = getPackageForTier(tier);
    if (pkg) return pkg.product.priceString;
    // Fallback prices while loading
    if (tier === 'pro') return billingInterval === 'year' ? '$79/yr' : '$9/mo';
    if (tier === 'enterprise') return billingInterval === 'year' ? '$199/yr' : '$19/mo';
    return '—';
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const tiers = [
    { key: 'free',       label: 'Free',       desc: 'Get started',           color: 'bg-muted' },
    { key: 'pro',        label: 'Pro',         desc: 'For serious creators',  color: 'bg-primary/10', highlight: true },
    { key: 'enterprise', label: 'Enterprise',  desc: 'For studios & teams',   color: 'bg-muted' },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">

      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold">Choose your plan</h1>
        <p className="text-muted-foreground text-sm">Unlock the full Planbase experience</p>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3">
        <span className={`text-sm ${billingInterval === 'month' ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>Monthly</span>
        <button
          onClick={() => setBillingInterval(b => b === 'month' ? 'year' : 'month')}
          className={`relative w-11 h-6 rounded-full transition-colors ${billingInterval === 'year' ? 'bg-primary' : 'bg-muted-foreground/30'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${billingInterval === 'year' ? 'translate-x-5' : ''}`} />
        </button>
        <span className={`text-sm ${billingInterval === 'year' ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
          Annual <span className="text-green-600 text-xs font-semibold">Save 30%</span>
        </span>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tiers.map(({ key, label, desc, highlight }) => {
          const isCurrent = currentTier === key;
          const features = PLAN_FEATURES[key] ?? [];
          const pkg = iapAvailable ? getPackageForTier(key) : null;
          const webPlan = plans.find(p => p.name?.toLowerCase().includes(key) || p.id?.toLowerCase().includes(key));

          return (
            <div
              key={key}
              className={`rounded-2xl border p-6 flex flex-col gap-4 transition-all ${
                highlight
                  ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                  : 'border-border bg-card'
              } ${isCurrent ? 'opacity-90' : ''}`}
            >
              {/* Plan header */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-base">{label}</span>
                  {isCurrent && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                      Current
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>

              {/* Price */}
              <div>
                <span className="text-3xl font-bold">{getPriceDisplay(key)}</span>
                {key !== 'free' && (
                  <span className="text-xs text-muted-foreground ml-1">/{billingInterval === 'year' ? 'yr' : 'mo'}</span>
                )}
              </div>

              {/* Features */}
              <ul className="flex-1 space-y-2">
                {features.map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <CheckIcon className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                    <span className="text-foreground/80">{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              {key === 'free' ? (
                <button disabled className="w-full h-10 rounded-xl bg-muted text-muted-foreground text-sm font-medium cursor-not-allowed">
                  {isCurrent ? 'Your current plan' : 'Always free'}
                </button>
              ) : isCurrent ? (
                !iapAvailable ? (
                  <button
                    onClick={handleManageBilling}
                    disabled={portalLoading}
                    className="w-full h-10 rounded-xl border border-border bg-card text-sm font-medium hover:bg-muted transition-colors"
                  >
                    {portalLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin mx-auto" /> : 'Manage billing'}
                  </button>
                ) : (
                  <button disabled className="w-full h-10 rounded-xl bg-muted text-muted-foreground text-sm font-medium cursor-not-allowed">
                    Active plan
                  </button>
                )
              ) : (
                <button
                  onClick={() => {
                    if (iapAvailable && pkg) {
                      handleNativePurchase(pkg);
                    } else if (!iapAvailable) {
                      handleWebCheckout(`${key}_${billingInterval}`);
                    }
                  }}
                  disabled={!!purchasing || planLoading || (!pkg && iapAvailable)}
                  className={`w-full h-10 rounded-xl text-sm font-semibold transition-colors ${
                    highlight
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'border border-border bg-card hover:bg-muted'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {purchasing === (pkg?.identifier ?? webPlan?.id)
                    ? <ArrowPathIcon className="h-4 w-4 animate-spin mx-auto" />
                    : `Upgrade to ${label}`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Restore purchases — native only */}
      {iapAvailable && (
        <div className="text-center">
          <button
            onClick={handleRestore}
            disabled={restoring}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {restoring
              ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
              : <ArrowUturnLeftIcon className="h-3.5 w-3.5" />}
            Restore purchases
          </button>
        </div>
      )}

      {/* Legal */}
      <p className="text-center text-xs text-muted-foreground px-4">
        {iapAvailable
          ? 'Subscriptions are billed through your Apple ID. Cancel any time in Settings → Subscriptions.'
          : 'Subscriptions are billed via Stripe. Cancel anytime.'}
      </p>
    </div>
  );
}
